import { post, read } from '@/utils/request'
import type { ListData } from 'bv-typings'
import type { IAccountCheckingInfo } from '@/data/columns/accountCheck'
import { toggleTime } from '@/utils'
import { isStr, isValue } from 'bv-validator'
import { onErrorNotice } from 'bv-utils'
import type { IAccountViolationInfo } from '@/data/columns/accountCheck'
import type { IBlockedAccountWhitelist } from '@/pages/AccountCheck/BlockedAccountWhitelist/interface'
import type { IBlockedSignWhitelist } from '@/pages/AccountCheck/BlockedSignWhitelist/interface'

export type IGetAccountCheckListParams = {
  start?: string;
  end?: string;
  accountId?: string;
  accountName?: string;
  accountStatus?: string; // 多选
  submitter?: string;
  status?: string; // 多选
  type?: string; // 多选
}

export type IAccountCheckType = 'checking' | 'violation' | 'closed' | 'blocked-account-list' | 'blocked-sign-list' | ''

export type IAccountCheckingListRes = ListData<IAccountCheckingInfo>

export type IAccountViolationListRes = ListData<IAccountCheckingInfo>

export type IAccountClosedListRes = ListData<IAccountCheckingInfo>

function getCheckingList (params?: IGetAccountCheckListParams) {
  return read<IAccountCheckingListRes>(
    'account_check/check_account_list',
    toggleTime(params),
    '查询列表失败',
  )
}

function getViolationList (params?: IGetAccountCheckListParams) {
  return read<IAccountViolationListRes>(
    'account_check/violations',
    toggleTime(params),
    // '查询列表失败'
  )
}

function getClosedList (params?: IGetAccountCheckListParams) {
  return read<IAccountClosedListRes>(
    'user_account/fb_disabled_account',
    params,
    // '查询列表失败',
  )
}

const transformParams = (params?: IBlockedAccountWhitelist.Request.AccountWhitelistParams) => {
  const accountIds = params?.accountIds
  const signIds = params?.signIds
  const userEmails = params?.userEmails
  const dateRange = params?.dateRange
  delete(params?.dateRange)

  return {
    ...params,
    accountIds: isStr(accountIds) ? [ accountIds ] : accountIds,
    signIds: isStr(signIds) ? [ signIds ] : signIds,
    userEmails: isStr(userEmails) ? [ userEmails ] : userEmails,
    startTime: dateRange?.[0],
    endTime: dateRange?.[1],
  }
}
// 获取封停账户白名单列表
function getBlockedAccountList (params?: IBlockedAccountWhitelist.Request.AccountWhitelistParams) {
  return post<ListData<IBlockedAccountWhitelist.Response.AccountWhitelistInfo>>(
    'user_account/fb_disabled_account_white/list',
    transformParams(params),
  )
}
// 获取封禁签约白名单列表
function getBlockedSignList (params?: IBlockedSignWhitelist.Request.SignWhitelistParams) {
  return post<ListData<IBlockedAccountWhitelist.Response.AccountWhitelistInfo>>(
    'user_account/fb_disabled_sign_white/list',
    transformParams(params),
  )
}
// 批量删除封停账户白名单
const batchDeleteAccountWhitelist = (ids?: number[]) => post('user_account/fb_disabled_account_white/del', { ids }, '批量删除失败')
// 批量删除封停签约白名单
const batchDeleteSignWhitelist = (ids?: number[]) => post('user_account/fb_disabled_sign_white/del', { ids }, '批量删除失败')
// 批量添加封停账户白名单
const addAccountWhitelist = (params: { accountId: string; index: number }[], showNotice = true) => post('user_account/fb_disabled_account_white', params, showNotice ? '添加失败' : undefined)
// 添加封禁签约白名单签约
const addSignWhitelist = (params: { saleId: number; signId: number }) => post('user_account/fb_disabled_sign_white', params, '添加失败')

const getList = {
  checking: getCheckingList,
  violation: getViolationList,
  closed: getClosedList,
  'blocked-account-list': getBlockedAccountList,
  'blocked-sign-list': getBlockedSignList,
}

async function addClosedAccount (params?: { accountId: string }) {
  const [ res, error ] = await post(
    'user_account/fb_disabled_account',
    [ params ],
    // '添加封停账户失败',
  )
  if (isValue(error)) {
    onErrorNotice('添加封停账户失败', error?.[0].errorInfo ?? error.toString())
  }
  return [ res, error ]
}

function addClosedAccounts (params?: any) {
  return post('user_account/fb_disabled_account', params)
}

function getViolationDetail (id: string) {
  return read<any>('account_check/violations', undefined, '获取详情失败', id)
}

function getClosedDetail (id: string) {
  return read<any>('user_account/fb_disabled_account', undefined, '获取详情失败', id)
}

export type IClosedAccountSpendInfo = {
  accountId: string
  accountName: string
  accountStatus: number
  balance: string
  cost: string
  recharge: string
}

function searchClosedAccounts (accountIds?: string[]) {
  return post<IClosedAccountSpendInfo[]>('user_account/fb_account_view', { accountIds }, '查询账户详情失败')
}

const jumpToAuth = () => {
  return read('update_token/update_token', undefined, '获取授权链接失败')
}

export type AccountClearInfo = {
  accountId: string
  accountClearRecords: {
    clearDate: string //清零时间
    clearSpendCap: number  //清零前额度
    clearAmountSpent: number //清零前消耗
  }[]
}

const searchClearAccounts = (accountIds?: string[]) => {
  return post<AccountClearInfo[]>('user_account/fb_account_record_view', { accountIds }, '查询账户清零详情失败')
}

async function editViolation (params: IAccountViolationInfo, id: string) {
  return post<any> (`account_check/violations_update`, params, '修改信息失败', id)
}

async function addViolationAccount (params: { accountId: string; violateAdvertiseingPolicies: number; reason?: string }) {
  const { violateAdvertiseingPolicies } = params
  Object.assign(params, {
    violateAdvertiseingPolicies: violateAdvertiseingPolicies ? 'yes' : 'no'
  })
  const [ res, error ] = await post(
    'account_check/violations',
    params,
    // '添加封停账户失败',
  )
  if (isValue(error)) {
    onErrorNotice('添加违规账户失败', error?.[0].errorInfo ?? error.toString())
  }
  return [ res, error ]
}

export {
  getList,
  getCheckingList,
  getViolationList,
  getClosedList,
  addClosedAccount,
  getViolationDetail,
  addClosedAccounts,
  getClosedDetail,
  searchClosedAccounts,
  jumpToAuth,
  searchClearAccounts,
  editViolation,
  addViolationAccount,
  getBlockedAccountList,
  getBlockedSignList,
  batchDeleteAccountWhitelist,
  batchDeleteSignWhitelist,
  addAccountWhitelist,
  addSignWhitelist,
}
